import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormControl } from '@angular/forms';
import { DetailsFormOptions } from './details-form-options.interface';
import { toFormlyOptions, isRapidProgram } from './details-form-helpers';

/**
 * SECTION 2.2: CLIENT REFERRAL INFORMATION
 */
export function getClientReferralFields(options: DetailsFormOptions): FormlyFieldConfig[] {
	return [
		{
			fieldGroupClassName: 'row',
			fieldGroup: [
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Account_Name',
					type: 'searchable-select',
					props: {
						label: 'Agency',
						options: options.agencies$,
						valueProp: 'id',
						labelProp: 'Account_Name',
						searchPlaceholder: 'Search agencies',
						noEntriesFoundLabel: 'No matching agencies found',
						change: (field: any, _event: any) => {
							// Reset the selected collector after changing the agency
							const collectorFormControl: FormControl =
								field.form?.get('Referred_By');
							collectorFormControl?.setValue(null);
							collectorFormControl?.markAsTouched();
						},
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							!isRapidProgram(field),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Sales_Agent_ID',
					type: 'select',
					props: {
						label: 'Sales Agent',
						options: toFormlyOptions(options.salesAgents$, "employeeID", "full_name"),
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							!isRapidProgram(field),
						// Anyone may assign a sales agent while there is none; once one is saved, only users who can edit it may change it
						'props.disabled': (field: FormlyFieldConfig) =>
							field.model?._canEditSalesAgent !== true && field.model?._hasSavedSalesAgent === true,
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Referred_By',
					type: 'collector-selector',
					props: {
						label: 'Collector',
						placeholder: 'Click the icon to select a collector',
						getAgencyName: options.getAgencyName,
						onCollectorButtonClick: options.onCollectorButtonClick,
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							!isRapidProgram(field),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Loan_Type',
					type: 'select',
					props: {
						label: 'Loan Type',
						options: toFormlyOptions(options.loanTypes$),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Loyalty_Payment_Plan',
					type: 'select',
					props: {
						label: 'Loyalty Payment Plan',
						options: [
							{ value: 'Yes', label: 'Yes' },
							{ value: 'No', label: 'No' },
						],
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							isRapidProgram(field),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Agency_Location',
					type: 'select',
					props: {
						label: 'Agency Location',
						options: toFormlyOptions(options.agencyLocations$),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Broker',
					type: 'select',
					props: {
						label: 'Broker',
						options: toFormlyOptions(options.brokers$),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Creditt',
					type: 'searchable-select',
					props: {
						label: 'Creditor',
						options: options.creditors$,
						valueProp: 'display_value',
						labelProp: 'display_value',
						searchPlaceholder: 'Search creditors',
						noEntriesFoundLabel: 'No matching creditors found',
						allowNewLabel: true,
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							!isRapidProgram(field),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Creditor_Agency_Name',
					type: 'input',
					props: {
						label: 'Creditor Details',
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Referral_type',
					type: 'select',
					props: {
						label: 'Referral method',
						options: toFormlyOptions(options.referralMethods$),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Referral_Date',
					type: 'datepicker',
					props: {
						label: 'Referral Date',
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Closing_Date',
					type: 'datepicker',
					props: {
						label: 'Closing Date',
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Acc_Number',
					type: 'input',
					props: {
						label: 'Account Number (EDP)',
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Account_Bal',
					type: 'currency',
					props: {
						label: 'Account Balance',
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							!isRapidProgram(field),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Creditor_Portfolio',
					type: 'select',
					props: {
						label: 'Creditor Portfolio',
						options: toFormlyOptions(options.creditorPortfolios$),
					},
					expressions: {
						'props.required': (field: FormlyFieldConfig) =>
							!isRapidProgram(field),
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Custom_Settlement',
					type: 'select',
					props: {
						label: 'Custom Settlement?',
						options: [
							{ value: 'Yes', label: 'Yes' },
							{ value: 'No', label: 'No' },
						],
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Custom_Agency_Amount',
					type: 'currency',
					props: {
						label: 'Custom Agency Amount',
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Legal_Fee',
					type: 'currency',
					props: {
						label: 'Legal Fees',
					},
					expressions: {
						'props.disabled': 'true', // Disables icon and input
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Settlement_Amount',
					type: 'currency',
					props: {
						label: 'Settlement Amount',
					},
					expressions: {
						'props.disabled': 'true', // Disables icon and input
					},
				},
				{
					className: 'col-sm-6 col-lg-4',
					key: 'Total_Legal_Fees',
					type: 'currency',
					props: {
						label: 'Total Legal Fees (SAHA)',
					},
					expressions: {
						'props.disabled': 'true', // Disables icon and input
					},
				},
			],
		},
		// Additional Debt FormArray
		{
			className: 'd-block mt-3',
			fieldGroup: [
				{
					key: 'Additional_Debt',
					type: 'repeat',
					props: {
						label: 'Additional debts',
						addText: 'Add Additional Debt',
						addIcon: 'add',
						emptyMessage: 'No additional debts added yet',
					},
					fieldArray: {
						fieldGroupClassName: 'row panel-small',
						fieldGroup: [
							{
								className: 'col-6 col-md-2',
								key: 'Type_of_Credit',
								type: 'select',
								props: {
									label: 'Type of Credit',
									options: toFormlyOptions(options.loanTypes$),
									panelClass: 'panel-small-select',
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Agency',
								type: 'searchable-select',
								props: {
									label: 'Agency',
									options: options.agencies$,
									valueProp: 'id',
									labelProp: 'Account_Name',
									searchPlaceholder: 'Search Agencies',
									noEntriesFoundLabel: 'No matching agencies found',
									panelClass: 'panel-small-select',
									change: (field: any, _event: any) => {
										// Reset the selected collector after changing the agency
										const collectorFormControl: FormControl =
											field.form?.get('Collector');
										collectorFormControl?.setValue(null);
										collectorFormControl?.markAsTouched();
									},
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Collector',
								type: 'collector-selector',
								props: {
									label: 'Collector',
									placeholder: 'Select',
									getAgencyName: options.getAgencyName,
									onCollectorButtonClick: options.onCollectorButtonClick,
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'misc',
								type: 'searchable-select',
								props: {
									label: 'Creditor',
									options: options.creditors$,
									valueProp: 'display_value',
									labelProp: 'display_value',
									searchPlaceholder: 'Search creditors',
									noEntriesFoundLabel: 'No matching creditors found',
									allowNewLabel: true,
									panelClass: 'panel-small-select',
									required: true,
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Account_Number',
								type: 'input',
								props: {
									label: 'Account Number',
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Creditor_Portfolio',
								type: 'select',
								props: {
									label: 'Creditor Portfolio',
									options: toFormlyOptions(options.additionalDebtCreditorPortfolios$),
									panelClass: 'panel-small-select',
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Custom_Settlement',
								type: 'select',
								props: {
									label: 'Custom Settlement',
									required: true,
									panelClass: 'panel-small-select',
									options: [
										{ value: '', label: '' },
										{ value: 'Yes', label: 'Yes' },
										{ value: 'No', label: 'No' },
									],
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Account_Balance',
								type: 'currency',
								props: {
									label: 'Account Balance',
								},
								expressions: {
									'props.required': (field: FormlyFieldConfig) => {
										return !!field.model?.Creditor_Portfolio;
									},
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Custom_Agency_Amount',
								type: 'currency',
								props: {
									label: 'Custom Agency Amount',
								},
								expressions: {
									'props.required': (field: FormlyFieldConfig) => {
										return field.model?.Custom_Settlement === 'Yes';
									},
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Legal_Fee',
								type: 'currency',
								props: {
									label: 'Legal Fee',
									readonly: true,
								},
							},
							{
								className: 'col-6 col-md-2',
								key: 'Settlement_Amount',
								type: 'currency',
								props: {
									label: 'Settlement Amount',
									readonly: true,
								},
							},
						],
					},
				},
			],
		},
	];
}